import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Typography, 
  Container, 
  Paper, 
  Alert, 
  CircularProgress,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { useAuth } from '@/context/AuthProvider';
import { API_BASE_URL } from '@/config';

interface Artist {
  id: string;
  name: string;
}

interface Album {
  id: string;
  title: string;
  artist_id: string;
  artwork_url: string;
  access_type: string;
  release_date: string;
}

interface Track {
  id: string;
  title: string;
  track_number: number;
  album_id: string;
}

const AlbumCheckoutPage = () => {
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { id: albumId } = useParams<{ id: string }>();

  const [artist, setArtist] = useState<Artist | null>(null);
  const [album, setAlbum] = useState<Album | null>(null);
  const [tracks, setTracks] = useState<Track[]>([]);

  // Stripe price mapping for different access types
  const priceMapping = {
    soundcheck: 'price_1RYoFNER541rKiq9ubgY8m41', // £5.99
    // Add more access types and their price IDs here
  };

  const accessTypeInfo = {
    soundcheck: {
      name: 'Soundcheck Access',
      price: '£5.99',
      description: [
        '• Exclusive behind-the-scenes content',
        '• Early access to new releases', 
        '• Special acoustic versions',
        '• Artist commentary and insights'
      ]
    }
  };

  useEffect(() => {
    const loadMetadata = async () => {
      if (!albumId) {
        setError('Album ID not provided');
        setDataLoading(false);
        return;
      }

      try {
        setDataLoading(true);
        
        // Load album data
        const albumRes = await fetch(`${API_BASE_URL}/public/album/${albumId}`);
        if (!albumRes.ok) throw new Error('Album not found');
        const albumData = await albumRes.json();
        setAlbum(albumData);

        // Load artist data
        const artistRes = await fetch(`${API_BASE_URL}/public/artist/${albumData.artist_id}`);
        if (!artistRes.ok) throw new Error('Artist not found');
        const artistData = await artistRes.json();
        setArtist(artistData);

        // Load tracks for this album
        const tracksRes = await fetch(`${API_BASE_URL}/public/track?album=${albumId}`);
        if (tracksRes.ok) {
          const tracksData = await tracksRes.json();
          
          // The API returns { page, limit, tracks } format
          const tracks = tracksData.tracks || [];
          
          // Sort tracks by track number
          const sortedTracks = tracks
            .filter((track: Track) => track.album_id === albumId)
            .sort((a: Track, b: Track) => (a.track_number || 0) - (b.track_number || 0));

          setTracks(sortedTracks);
        }
      } catch (err) {
        console.error('Failed to load album metadata', err);
        setError(err instanceof Error ? err.message : 'Failed to load album information');
      } finally {
        setDataLoading(false);
      }
    };

    loadMetadata();
  }, [albumId]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  const handleCheckout = async () => {
    if (!user || !album) {
      navigate('/login');
      return;
    }

    const accessType = album.access_type as keyof typeof priceMapping;
    const priceId = priceMapping[accessType];

    if (!priceId) {
      setError(`No price configured for access type: ${accessType}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/stripe/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: user.id,
          price_id: priceId,
          success_url: `${window.location.origin}/payment-success`,
          cancel_url: `${window.location.origin}/payment-cancelled`,
          metadata: {
            album_id: albumId,
            user_id: user.id,
            access_type: accessType
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking authentication or loading data
  if (authLoading || dataLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!user) {
    return null;
  }

  // Show error if album not found
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!album || !artist) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="warning">Album or artist information not available</Alert>
      </Container>
    );
  }

  const accessType = album.access_type as keyof typeof accessTypeInfo;
  const accessInfo = accessTypeInfo[accessType];

  if (!accessInfo) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="warning">Access type "{album.access_type}" not supported for purchase</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Purchase {accessInfo.name}
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
            {/* Album Artwork */}
            {album.artwork_url && (
              <Box 
                component="img" 
                src={album.artwork_url} 
                alt={album.title} 
                sx={{ 
                  width: 200, 
                  height: 200, 
                  objectFit: 'cover',
                  borderRadius: 2,
                  boxShadow: 3
                }} 
              />
            )}
            
            {/* Album Details */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" gutterBottom>{album.title}</Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                by {artist.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Released: {new Date(album.release_date).getFullYear()}
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Chip 
                  label={accessInfo.name} 
                  color="primary" 
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
                <Chip 
                  label={accessInfo.price} 
                  color="success" 
                  variant="filled"
                />
              </Box>
            </Box>
          </Box>

          {/* What's Included */}
          <Card sx={{ mb: 3, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🎵 {accessInfo.name} Includes:
              </Typography>
              {accessInfo.description.map((item, index) => (
                <Typography key={index} variant="body2">
                  {item}
                </Typography>
              ))}
            </CardContent>
          </Card>

          {/* Track Listing */}
          {tracks.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Track Listing ({tracks.length} tracks)
                </Typography>
                <Box component="ol" sx={{ pl: 2 }}>
                  {tracks.map(track => (
                    <Typography component="li" key={track.id} variant="body2" sx={{ mb: 0.5 }}>
                      {track.title}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* User Info */}
        <Card sx={{ mb: 3, backgroundColor: 'white', border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
              Purchase Details
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              User: {user.email}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              Access Type: {accessInfo.name}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              Price: {accessInfo.price} GBP
            </Typography>
          </CardContent>
        </Card>

        {/* Purchase Button */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleCheckout}
            disabled={loading}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              backgroundColor: '#FF0054',
              '&:hover': {
                backgroundColor: '#E0004A',
              },
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Redirecting to Stripe...
              </Box>
            ) : (
              `Purchase ${accessInfo.name} - ${accessInfo.price}`
            )}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 4, p: 2, backgroundColor: '#2c2c2c', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ color: '#ffffff' }}>
            <strong>Test Mode:</strong> This will use Stripe's test environment. 
            Use test card number 4242 4242 4242 4242 with any future expiry date and any CVC.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default AlbumCheckoutPage; 